// Zod schemas for request validation at the worker edge. Kept in shared so the
// client can reuse them for optimistic local validation.
import { z } from "zod";

const int64String = z
  .string()
  .regex(/^-?\d+$/, "must be a 64-bit integer string");

// Literal enum so zod infers the PeerType union (not plain string).
export const peerTypeSchema = z.enum([
  "group",
  "supergroup",
  "channel",
  "user",
  "bot",
]);

export const targetFilterSchema = z.object({
  mode: z.enum(["all", "name", "tags"]),
  q: z.string().max(256).optional(),
  tagIds: z.array(z.number().int().positive()).max(200).optional(),
  tagMode: z.enum(["any", "all"]).optional(),
  includeTypes: z.array(peerTypeSchema).optional(),
  excludeTypes: z.array(peerTypeSchema).optional(),
  activeOnly: z.boolean().optional(),
  canSendOnly: z.boolean().optional(),
});

export const activeHoursSchema = z
  .object({
    startHour: z.number().int().min(0).max(23),
    endHour: z.number().int().min(0).max(23),
    tz: z.string().min(1).max(64),
  })
  .nullable();

export const paceSettingsSchema = z.object({
  minDelayS: z.number().int().min(0).max(86400),
  maxDelayS: z.number().int().min(0).max(86400),
  dailyCap: z.number().int().min(1).max(100000),
  pauseEveryN: z.number().int().min(1).max(100000).nullable().optional(),
  pauseSecs: z.number().int().min(0).max(86400).nullable().optional(),
  scheduledStartAt: z.number().int().nullable().optional(),
  activeHours: activeHoursSchema.optional(),
});

export const dialogUpsertSchema = z.object({
  peerId: int64String,
  accessHash: int64String.nullable(),
  peerType: peerTypeSchema,
  title: z.string().max(512),
  username: z.string().max(64).nullable(),
  canSend: z.boolean(),
  canSendReason: z.string().max(64).nullable(),
  isCreator: z.boolean(),
  archived: z.boolean(),
});

export const bulkUpsertSchema = z.object({
  marker: z.number().int(),
  rows: z.array(dialogUpsertSchema).max(200),
});

export const createTagSchema = z.object({
  name: z.string().min(1).max(64),
  color: z.string().max(16).optional(),
});

export const updateTagSchema = z.object({
  name: z.string().min(1).max(64).optional(),
  color: z.string().max(16).optional(),
});

export const tagAssignSchema = z.object({
  groupIds: z.array(int64String).min(1).max(100),
});

export const tagAssignByFilterSchema = z.object({
  filter: targetFilterSchema,
});

export const mediaItemSchema = z.object({
  key: z.string().min(1).max(256),
  w: z.number().int().nullable(),
  h: z.number().int().nullable(),
  mime: z.string().max(128),
  size: z.number().int(),
});

export const putDraftSchema = z.object({
  captionTemplate: z.string().max(8192).optional(),
  media: z.array(mediaItemSchema).max(10).optional(),
  baseFileName: z.string().max(128).optional(),
  varyCaption: z.boolean().optional(),
  varyFilename: z.boolean().optional(),
  varyBytes: z.boolean().optional(),
});

export const createBroadcastSchema = z.object({
  name: z.string().max(128).optional(),
  draftId: z.number().int().positive(),
  targetFilter: targetFilterSchema,
  pace: paceSettingsSchema,
  mode: z.enum(["send", "edit"]).optional(),
  editOf: z.number().int().positive().optional(),
});

const sendResultSchema = z.enum(["sent", "skipped", "failed", "deferred"]);
export const reportSchema = z.object({
  items: z
    .array(
      z.object({
        targetId: z.number().int().positive(),
        result: sendResultSchema,
        variant: z.string().max(8192).optional(),
        errorCode: z.string().max(64).optional(),
        deferSeconds: z.number().int().min(0).max(86400).optional(),
        messageIds: z.array(int64String).max(10).optional(),
      }),
    )
    .max(50),
});

export const nextBatchSchema = z.object({
  limit: z.number().int().min(1).max(50).optional(),
});

export const putAccountSchema = z.object({
  tgUserId: int64String.nullable(),
  username: z.string().max(64).nullable(),
});
